import { useEffect } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { CustomerAuthPanel } from "@/pages/Auth";

/**
 * Customer sign-in as a card over the page it was opened from, instead of a navigation to /login —
 * the landing page stays behind it, dimmed, so closing returns the visitor exactly where they were.
 * The `/login` route still exists and renders the same {@link CustomerAuthPanel}; this is an
 * alternative entry point, not a replacement.
 *
 * Dismissable three ways (close button, backdrop, Escape). On a successful sign-in the panel
 * navigates to /customer and calls `onClose`, so the dialog is never left open behind the portal.
 */
export default function SignInDialog({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    // Freeze the page underneath — otherwise a scroll gesture over the dim moves the landing page.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          data-testid="signin-dialog"
        >
          <div
            className="absolute inset-0 bg-[#080C16]/80 backdrop-blur-sm"
            onClick={onClose}
            data-testid="signin-dialog-backdrop"
            aria-hidden
          />
          <motion.div
            role="dialog" aria-modal="true" aria-label="Sign in"
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-sm bg-[#111623] border border-white/10 rounded-2xl p-6 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)]"
          >
            <button
              type="button" onClick={onClose} aria-label="Close sign in"
              data-testid="signin-dialog-close"
              className="absolute right-4 top-4 text-[#A0AABF] hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#A0AABF]">Customer Portal</div>
            <div className="font-serif-display text-2xl tracking-tight mt-1 mb-6">One tap to your legal workspace</div>
            <CustomerAuthPanel onSignedIn={onClose} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
